import axios from 'axios';
import { sessionManager } from './sessionManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Track if we're currently refreshing to avoid multiple refresh requests
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Add subscribers waiting for token refresh
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers when token is refreshed
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Refresh access token using refresh token
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = sessionManager.getRefreshToken();

    if (!refreshToken) {
      console.warn('No refresh token available');
      return null;
    }

    const response = await axios.post(`${API_URL}/auth/user/refresh-token`, {
      refreshToken,
    });

    if (response.data.success && response.data.token) {
      const newToken = response.data.token;

      // Update session with new token
      sessionManager.updateToken(newToken);

      // Update user permissions if they changed
      if (response.data.user?.permissions) {
        sessionManager.updateUserPermissions(response.data.user.permissions, response.data.user.customPermissions);
      }

      console.log('✅ Access token refreshed successfully');
      return newToken;
    }

    return null;
  } catch (error) {
    console.error('❌ Failed to refresh access token:', error);
    return null;
  }
};

// Create axios instance with base configuration
const api = axios.create({
  timeout: 10000,
  // Remove default Content-Type to allow browser to set it automatically for FormData
  // headers: {
  //   'Content-Type': 'application/json',
  // },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionManager.getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['x-auth-token'] = token;

      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
    } else {
      console.warn('No auth token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle auth errors and auto-refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = originalRequest?.url || '';

      // Don't redirect on login/register failures - let the component handle it
      if (url.includes('/auth/user/login') || url.includes('/auth/employee/login') || url.includes('/register')) {
        return Promise.reject(error);
      }

      // Don't retry refresh token endpoint
      if (url.includes('/auth/user/refresh-token')) {
        console.warn('Refresh token expired, redirecting to login');
        sessionManager.clearSession();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Mark this request as retried
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        const newToken = await refreshAccessToken();

        isRefreshing = false;

        if (newToken) {
          // Notify all waiting requests
          onTokenRefreshed(newToken);

          // Retry original request with new token
          originalRequest.headers['x-auth-token'] = newToken;
          return api(originalRequest);
        } else {
          // Refresh failed, clear session and redirect to login
          console.warn('Token refresh failed, redirecting to login');
          sessionManager.clearSession();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } else {
        // If already refreshing, wait for the new token
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers['x-auth-token'] = token;
            resolve(api(originalRequest));
          });
        });
      }
    }

    // Handle archived client accounts: backend signals this with 403 + msg === 'client_archived'
    if (error.response?.status === 403 && error.response?.data?.msg === 'client_archived') {
      const clientName = error.response.data.clientName || '';
      if (clientName) {
        sessionStorage.setItem('archivedClientName', clientName);
      }
      sessionManager.clearSession();
      try {
        const url = new URL(window.location.href);
        url.pathname = '/login';
        url.searchParams.set('archived', '1');
        window.location.href = url.toString();
      } catch {
        window.location.href = '/login?archived=1';
      }
      return Promise.reject(error);
    }

    // Handle inactive user accounts: backend signals this with 403 + msg === 'account_inactive'
    if (error.response?.status === 403 && error.response?.data?.msg === 'account_inactive') {
      sessionManager.clearSession();
      try {
        const url = new URL(window.location.href);
        url.pathname = '/login';
        url.searchParams.set('inactive', '1');
        window.location.href = url.toString();
      } catch {
        window.location.href = '/login?inactive=1';
      }
      return Promise.reject(error);
    }

    // Billing suspension (middleware / API 403)
    if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_SUSPENDED_PAYMENT') {
      sessionManager.clearSession();
      try {
        const url = new URL(window.location.href);
        url.pathname = '/login';
        url.searchParams.set('suspended', '1');
        window.location.href = url.toString();
      } catch {
        window.location.href = '/login?suspended=1';
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;
